import styles from '../DavidPifView.module.css'
import classNames from 'classnames'
import { CelebrationIcon } from '@/atoms/Icons/CelebrationIcon'
import { Image } from '@/atoms/Image'
import { ParagraphLG } from '@/atoms/Text'
import { slugs } from '@/constants/slugs'
import { useTranslate } from '@/utils/translate/translate-client'
import useTheatricalGoalService from '@/views/TicketCheckoutViews/hooks/useTheatricalGoalService'
import { DavidButton } from './DavidButton'

const imgInspireGen = '/v1760914561/theatrical/david/dirt-texture-on-white-01'

interface CounterProps {
  releaseDate?: string
}

function Counter({ releaseDate }: CounterProps) {
  const { t } = useTranslate('pif')
  const { goalLineInfo } = useTheatricalGoalService({
    theatricalSlug: slugs.david,
    releaseDate: releaseDate || '',
    usePifGoal: true,
  })

  const kidsReached = goalLineInfo.kidTicketsSold

  return (
    <div className="relative w-full rounded-[24px] px-[64px] py-8 md:p-[64px]" data-name="counter">
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 rounded-[24px]">
        <div className="absolute inset-0 rounded-[24px] bg-[#f9b74b]" />
        <Image
          alt=""
          className="rounded-[24px] object-cover opacity-80"
          src="/v1760542675/theatrical/david/ticket-pif-icon-collage"
          fill
          sizes="(max-width: 768px) 100vw, 50vw"
        />
      </div>
      <div className="flex size-full flex-col items-center justify-center">
        <div className="relative box-border flex w-full flex-col items-center justify-center gap-[32px]">
          <div className="relative flex w-full shrink-0 flex-col content-stretch items-center gap-[8px]">
            <div className="relative flex shrink-0 flex-col content-stretch items-center text-nowrap text-center not-italic leading-[0] text-[#411e09]">
              <div className="font-angelSans-medium relative flex shrink-0 flex-col justify-center text-[16px]">
                <p className="whitespace-pre text-nowrap leading-[20px]">
                  {t('kidsReachedToDate', 'Kids Reached to Date')}
                </p>
              </div>
              <div className="font-angel-royal relative flex shrink-0 flex-col justify-center text-[80px] md:text-[96px] md:tracking-[0.5px]">
                <p className="whitespace-pre text-nowrap leading-[64px] md:leading-[80px]">{kidsReached}</p>
              </div>
            </div>
            <div className="relative box-border flex h-[28px] shrink-0 content-stretch items-center justify-center gap-[4px] rounded-[100px] bg-[#fff4eb] px-[12px] py-[4px]">
              <div className="relative size-[14px] shrink-0">
                <CelebrationIcon className="block size-full" size={14} color="core-warning-800" />
              </div>
              <div className="font-angelSans-medium relative flex shrink-0 flex-col justify-center overflow-hidden text-ellipsis text-nowrap text-center text-[14px] not-italic leading-[0] text-[#763401]">
                <p className="overflow-hidden text-ellipsis whitespace-pre leading-[18px]">
                  {t('davidGiftedByExample', 'Diane from New York gifted 2 tickets')}
                </p>
              </div>
            </div>
          </div>
          <DavidButton variant="red" href="#" openPifOverlay>
            {t('helpKidsGoFree', 'Help Kids Go Free')}
          </DavidButton>
        </div>
      </div>
    </div>
  )
}

interface EndCtaSectionProps {
  releaseDate?: string
}

export function EndCtaSection({ releaseDate }: EndCtaSectionProps) {
  const { t } = useTranslate('pif')

  return (
    <section className={classNames('relative w-full', styles['section-md'])} data-name="end-cta">
      <div aria-hidden="true" className="pointer-events-none absolute inset-0">
        <div className="absolute inset-0 bg-[#faf0e7]" />
        <Image
          alt=""
          className="object-cover opacity-[0.55] mix-blend-color-burn"
          src={imgInspireGen}
          fill
          sizes="100vw"
          loading="lazy"
        />
      </div>
      <div
        className="relative mx-auto flex w-full max-w-[1440px] shrink-0 flex-col items-start gap-[40px] lg:flex-row lg:items-center lg:gap-[64px]"
        data-name="container-50-flip"
      >
        <Counter releaseDate={releaseDate} />
        <div className="relative w-full">
          <div className="flex size-full flex-col justify-center">
            <div className="relative box-border flex size-full flex-col content-stretch items-start justify-center gap-[32px] p-0 md:gap-[40px]">
              <div className="relative flex w-full flex-col items-start gap-[12px]">
                <p className="font-angel-royal relative w-full text-5xl lowercase not-italic leading-[2.5rem] text-[#141447] md:text-[3.5rem] md:leading-[3rem]">
                  {t('davidThousandsJoinedTitle', 'Thousands Have Already Paid It Forward – Join Them')}
                </p>

                <ParagraphLG>
                  {t(
                    'davidThousandsJoinedDescription',
                    'Every ticket builds momentum. Every family reached brings us closer to inspiring a generation.',
                  )}
                </ParagraphLG>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}
